import { memo, useCallback, useEffect, useReducer } from 'react';
import { Editor } from '@tiptap/core';
import { Button, Icon, TextContent } from '@amzn/awsui-components-core';
import {
  CommentAuthorType,
  InlineComment,
  InlineEditResolution,
  UserCommentState,
} from '@amzn/awscat-pattern-library-service-model';
import { PopoverDialog } from '../../v2-content-editor/PopoverDialog';
import FlashbarMessageBroker from '../../../common/FlashbarMessageBroker';
import styles from './SuggestionPopover.module.scss';
import { useInlineCommentManager } from '../../../contexts/InlineCommentManagerContext';
import { useInlineEditsManager } from '../../../contexts/InlineEditsManagerContext';
import { useSuggestionNavigationService } from '../../../contexts/SuggestionNavigationServiceContext';
import { useOpenInlineEdits } from '../../v2-hooks/useOpenInlineEdits';
import { NavigationItem, toKey } from '../../../services/SuggestionNavigationService';
import { log } from '../../../loggers/Logger';

/**
 * Where the popover is anchored and what accept/reject operates on.
 * Variants mirror {@link NavigationItem} from SuggestionNavigationService.
 */
export type SuggestionPopoverTarget =
  | {
      /** Popover for a particular ChangeVisual within an inline edit inside a TiptapEditor instance. */
      readonly type: 'doc_inline_edit';
      readonly inlineEditId: string;
      readonly target: DOMRect;
      readonly changeId: number;
      readonly editor: Editor;
      /** When true, accept/reject also resolves the InlineEdit in the backend. */
      readonly shouldResolveInlineEdit: boolean;
    }
  | {
      /** Popover for an inline comment anchored to a node in the editor. */
      readonly type: 'doc_inline_comment';
      readonly target: DOMRect;
      readonly commentId: string;
      readonly comment: InlineComment;
      readonly isCommentOnDeletedText: boolean;
    }
  | {
      /** Navigate to the container element holding the epic title, not the rich text field itself. */
      readonly type: 'epic_title_container';
      readonly inlineEditId: string;
      readonly epicId: string;
    }
  | {
      /** Navigate to the container element holding the epic description, not the rich text field itself. */
      readonly type: 'epic_description_container';
      readonly inlineEditId: string;
      readonly epicId: string;
    }
  | {
      /** Navigate to the container element holding the task title, not the rich text field itself. */
      readonly type: 'task_title_container';
      readonly inlineEditId: string;
      readonly taskId: string;
    }
  | {
      /** Navigate to the container element holding the task description, not the rich text field itself. */
      readonly type: 'task_description_container';
      readonly inlineEditId: string;
      readonly taskId: string;
    };

export interface SuggestionPopoverProps {
  readonly popoverTarget: SuggestionPopoverTarget;
  readonly onClose: () => void;
  /** Called after Reply reveals the comment thread in the sidebar. */
  readonly onCommentReply?: () => void;
  /** When true, accept/reject buttons are hidden and replies are directed to the Write comment pane. */
  readonly isReadonly?: boolean;
}

/**
 * Renders a popover with accept/reject buttons for an inline edit change
 * or an inline comment in a TiptapEditor instance.
 */
export const SuggestionPopover = memo(function SuggestionPopover({
  popoverTarget,
  onClose,
  onCommentReply,
  isReadonly = false,
}: SuggestionPopoverProps) {
  const navigationService = useSuggestionNavigationService();
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  // When an error is received, log and show a flash message
  function handleAcceptRejectError(err: unknown): void {
    const message =
      err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.';
    log.error({ err }, 'Failed to accept or reject the content');
    FlashbarMessageBroker.create().pushErrorMessage(message);
  }

  // When navigation state changes, re-render so hasPrev/hasNext stay current
  useEffect(() => {
    const unsub1 = navigationService.on('navigationItemChanged', forceUpdate);
    const unsub2 = navigationService.on('navigationStateReset', forceUpdate);
    forceUpdate();
    return () => {
      unsub1();
      unsub2();
    };
  }, [navigationService]);

  const inlineEditsManager = useInlineEditsManager();
  const inlineCommentManager = useInlineCommentManager();
  const openedEdits = useOpenInlineEdits();

  // After accept/reject, advance to the next item or wrap to the first
  const navigateToNextOrFirst = useCallback(() => {
    if (navigationService.hasNext) {
      navigationService.moveNext();
    } else {
      const first = navigationService.items[0];
      if (first) navigationService.moveInto(toKey(first));
    }
  }, [navigationService]);

  // When accept is clicked on an inline edit, apply the change and resolve if last in group
  // When accept is clicked on an inline comment, resolve the thread as IMPLEMENTED
  const handleAccept = useCallback(() => {
    if (popoverTarget.type === 'doc_inline_edit') {
      popoverTarget.editor.commands.acceptChange(popoverTarget.changeId);
      if (popoverTarget.shouldResolveInlineEdit) {
        inlineEditsManager
          .tryResolveEdit(popoverTarget.inlineEditId, InlineEditResolution.ACCEPTED)
          .catch(handleAcceptRejectError);
      }
    } else if (popoverTarget.type === 'doc_inline_comment') {
      inlineCommentManager
        .resolveThreadAs(popoverTarget.commentId, UserCommentState.IMPLEMENTED)
        .catch(handleAcceptRejectError);
    }
    onClose();
    navigateToNextOrFirst();
  }, [popoverTarget, inlineEditsManager, inlineCommentManager, onClose, navigateToNextOrFirst]);

  // When reject is clicked on an inline edit, revert the change and resolve if last in group
  // When reject is clicked on an inline comment, resolve the thread as IGNORED
  const handleReject = useCallback(() => {
    if (popoverTarget.type === 'doc_inline_edit') {
      popoverTarget.editor.commands.rejectChange(popoverTarget.changeId);
      if (popoverTarget.shouldResolveInlineEdit) {
        inlineEditsManager
          .tryResolveEdit(popoverTarget.inlineEditId, InlineEditResolution.REJECTED)
          .catch(handleAcceptRejectError);
      }
    } else if (popoverTarget.type === 'doc_inline_comment') {
      inlineCommentManager
        .resolveThreadAs(popoverTarget.commentId, UserCommentState.IGNORED)
        .catch(handleAcceptRejectError);
    }
    onClose();
    navigateToNextOrFirst();
  }, [popoverTarget, inlineEditsManager, inlineCommentManager, onClose, navigateToNextOrFirst]);

  const handleNavigateLeft = useCallback(() => navigationService.movePrev(), [navigationService]);
  const handleNavigateRight = useCallback(() => navigationService.moveNext(), [navigationService]);

  if (popoverTarget.type === 'doc_inline_edit') {
    const inlineEdit = openedEdits?.edits.find((e) => e.editId === popoverTarget.inlineEditId);
    const attribution = inlineEdit?.sourceAttributions?.[0];
    const title = attribution?.sourceScanner
      ? `${attribution.sourceScanner.scannerName} v${attribution.sourceScanner.scannerVersion}`
      : 'AI Agent Comment';
    const description = attribution?.justification ?? 'Suggestion below comes from AI agent';

    return (
      <PopoverDialog
        target={popoverTarget.target}
        icon={<Icon name="status-info" />}
        title={title}
        description={description}
        onClose={onClose}
        onAccept={isReadonly ? undefined : handleAccept}
        onReject={isReadonly ? undefined : handleReject}
        isNavigateLeftEnabled={navigationService.hasPrev}
        isNavigateRightEnabled={navigationService.hasNext}
        onNavigateLeft={handleNavigateLeft}
        onNavigateRight={handleNavigateRight}
      />
    );
  }

  if (popoverTarget.type === 'doc_inline_comment') {
    const { comment, isCommentOnDeletedText } = popoverTarget;

    // Accept/reject only applies to scanner comments — human comments are
    // conversational feedback that cannot be programmatically resolved.
    const isHumanComment = [CommentAuthorType.HUMAN, undefined].includes(comment.authorType);

    const handleReply = () => {
      inlineCommentManager.revealInSidebar(popoverTarget.commentId);
      onClose();
      onCommentReply?.();
    };

    const replyMessage = isReadonly ? (
      <TextContent>
        <span>
          You can use the Write comment pane to{' '}
          <Button variant="inline-link" onClick={handleReply}>
            reply
          </Button>{' '}
          to this comment
        </span>
      </TextContent>
    ) : (
      <TextContent>
        <span>
          You can also{' '}
          <Button variant="inline-link" onClick={handleReply}>
            reply
          </Button>{' '}
          to this comment
        </span>
      </TextContent>
    );

    return (
      <PopoverDialog
        target={popoverTarget.target}
        icon={<Icon name="status-info" />}
        title={
          comment.sourceAttributions?.[0]?.sourceScanner
            ? `${comment.sourceAttributions[0].sourceScanner.scannerName} v${comment.sourceAttributions[0].sourceScanner.scannerVersion}`
            : 'User Comment'
        }
        description={
          <TextContent>
            {isCommentOnDeletedText && (
              <p className={styles.deletedTextNotice}>This comment was made on deleted text</p>
            )}
            <blockquote className={styles.blockquote}>
              <em>{comment.commentData}</em>
            </blockquote>
            <div style={{ marginTop: 8 }}>{replyMessage}</div>
          </TextContent>
        }
        onClose={onClose}
        onAccept={isReadonly ? undefined : handleAccept}
        onReject={isReadonly ? undefined : handleReject}
        isAcceptEnabled={!isHumanComment}
        isRejectEnabled={!isHumanComment}
        isNavigateLeftEnabled={navigationService.hasPrev}
        isNavigateRightEnabled={navigationService.hasNext}
        onNavigateLeft={handleNavigateLeft}
        onNavigateRight={handleNavigateRight}
      />
    );
  }

  return null;
});
